// src/utils/rechercheGlobale.ts — SCE Issues
// La recherche de l'en-tête (Lot 9), élargie le 05/10 (maquette validée :
// https://claude.ai/artifact/ArzUFTyfjXggN1i2MPFScR, seconde rangée).
//
// Un seul champ, cinq sortes de résultats, dans cet ordre :
//
//   Projets          « 4334 » → la fiche projet PN4334 (CVE et client aussi)
//   Chefs de projet  « goy »  → ses projets
//   Filtrer          « atex 1 », « 100 bar », « custom » → la page Équipements,
//                    filtrée sur cette caractéristique du code de désignation
//   Problèmes        N°, titre, N° de série, OF, PN — et les références :
//                    FNC-T, NC-S, N° de plan, EDE
//   Équipements      N° de série, OF, PN, CVE — et NVS, code de désignation,
//                    modèle, chef de projet, client, caractéristiques
//
// Quand un résultat sort pour une raison qui ne se lit pas sur sa ligne (une
// référence FNC-T, un NVS…), `raison` la dit.
//
// Tout est déjà en mémoire : aucune requête par frappe. Pur : ni réseau, ni React.

import { cleRapprochement, estActif, type Equipement, type Issue } from '@/types/issue.types'
import type { Fnct } from '@/types/fnct.types'
import { debutFnct, estEnReleve } from './fnct'
import { ecartsLisibles, phraseDe } from './releveEcarts'
import {
  CRITERES,
  NON_RENSEIGNE,
  RACCOURCIS,
  lireEquipement,
  type Critere,
  type EquipementLu,
} from './filtresEquipements'

const LONGUEUR_MIN = 2
/** Une caractéristique se cherche à partir de 3 lettres : « 15 » ne doit pas proposer « 15 collectes ». */
const LONGUEUR_MIN_CARACTERISTIQUE = 3

export const LIMITES = { projet: 3, chef: 3, filtre: 2, probleme: 6, fnct: 4, equipement: 4 } as const

export type Resultat =
  | { genre: 'projet'; pn: string; cve: string; chef: string; client: string; nbEquipements: number; nbActifs: number }
  | { genre: 'chef'; nom: string; nbProjets: number; nbEquipements: number }
  | { genre: 'filtre'; critere: string; libelle: string; valeur: string; nb: number }
  | { genre: 'probleme'; issue: Issue; raison?: string }
  | { genre: 'equipement'; equipement: Equipement; raison?: string }
  | { genre: 'fnct'; numero: string; titre: string; machines: string; etat: 'releve' | 'ouverte' | 'close'; raison?: string }

/** Minuscules, sans accents, espaces simples : « Zone dangereuse » ≈ « zone dangéreuse ». */
export function normaliser(s: string | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

// ─── Le référentiel, indexé une fois ───

interface Caracteristique {
  critere: Critere
  valeur: string
  /** « zone dangereuse atex zone 1 » */
  texte: string
  /** « atex zone 1 » */
  valeurNormalisee: string
}

export interface ReferentielIndexe {
  lus: EquipementLu[]
  caracteristiques: Map<EquipementLu, Caracteristique[]>
}

/**
 * Critères qu'on peut chercher : pas le chef de projet (résultat à part), ni les
 * problèmes, ni la fabrication (la liste n'est pas lue par la recherche).
 */
const CRITERES_CHERCHABLES = CRITERES.filter((c) => !['chefProjet', 'problemes', 'fabrication'].includes(c.id))

function valeurCherchable(v: string): boolean {
  return v !== NON_RENSEIGNE && v !== 'Non' && !v.startsWith('Inconnu')
}

export function indexerReferentiel(equipements: readonly Equipement[]): ReferentielIndexe {
  const lus = equipements.map((e) => lireEquipement(e, 0))
  const caracteristiques = new Map<EquipementLu, Caracteristique[]>()
  for (const lu of lus) {
    const liste: Caracteristique[] = []
    for (const c of CRITERES_CHERCHABLES) {
      for (const valeur of c.valeurs(lu)) {
        if (!valeurCherchable(valeur)) continue
        liste.push({
          critere: c,
          valeur,
          texte: normaliser(`${c.libelle} ${valeur}`),
          valeurNormalisee: normaliser(valeur),
        })
      }
    }
    caracteristiques.set(lu, liste)
  }
  return { lus, caracteristiques }
}

/** Tous les mots tapés se retrouvent dans « libellé valeur », et au moins un dans la valeur. */
function correspond(c: Caracteristique, mots: readonly string[]): boolean {
  return mots.every((m) => c.texte.includes(m)) && mots.some((m) => c.valeurNormalisee.includes(m))
}

// ─── Recherche ───

function ms(iso: string | undefined): number {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isNaN(t) ? 0 : t
}

/** Les références d'un problème, avec leur nom, pour dire pourquoi il sort. */
function referencesDe(i: Issue): [string, string][] {
  const refs: [string, string][] = []
  if (i.refFnct) refs.push(['Réf. FNC-T', i.refFnct])
  if (i.refNcs) refs.push(['Réf. NC-S', i.refNcs])
  for (const plan of (i.numeroPlan ?? '').split(/\r?\n/)) if (plan.trim()) refs.push(['N° de plan', plan.trim()])
  if (i.numeroEDE) refs.push(['EDE', i.numeroEDE])
  return refs
}

export function rechercherProblemes(issues: readonly Issue[], q: string, n: number): Resultat[] {
  const cle = cleRapprochement(q)
  if (cle.length < LONGUEUR_MIN) return []
  const trouves: { issue: Issue; raison?: string }[] = []
  for (const i of issues) {
    const direct = [i.id, i.titre, i.numeroSerie, i.of, i.pn].some((v) => cleRapprochement(v).includes(cle))
    if (direct) {
      trouves.push({ issue: i })
      continue
    }
    const ref = referencesDe(i).find(([, v]) => cleRapprochement(v).includes(cle))
    if (ref) trouves.push({ issue: i, raison: `${ref[0]} : ${ref[1]}` })
    else if (i.sujet && cleRapprochement(i.sujet).includes(cle)) trouves.push({ issue: i, raison: `Sujet : ${i.sujet}` })
  }
  return trouves
    // Le N° exact d'abord (P2026-0042 tapé en entier), puis les plus récents.
    .sort(
      (a, b) =>
        Number(cleRapprochement(b.issue.id) === cle) - Number(cleRapprochement(a.issue.id) === cle) ||
        ms(b.issue.dateDeclaration) - ms(a.issue.dateDeclaration),
    )
    .slice(0, n)
    .map((t) => ({ genre: 'probleme' as const, ...t }))
}

export function rechercherEquipements(ref: ReferentielIndexe, q: string, n: number): Resultat[] {
  const cle = cleRapprochement(q)
  if (cle.length < LONGUEUR_MIN) return []
  const mots = normaliser(q).split(' ')
  const chercheCaracteristique = normaliser(q).length >= LONGUEUR_MIN_CARACTERISTIQUE
  // Rang : 0 = N° de série exact, 1 = identifiant, 2 = autre champ, 3 = caractéristique.
  const trouves: { lu: EquipementLu; rang: number; raison?: string }[] = []
  for (const lu of ref.lus) {
    const e = lu.equipement
    if ([e.numeroSerie, e.of, e.pn, e.cve].some((v) => cleRapprochement(v).includes(cle))) {
      trouves.push({ lu, rang: cleRapprochement(e.numeroSerie) === cle ? 0 : 1 })
      continue
    }
    const autres: [string, string | undefined, boolean][] = [
      ['Modèle', e.familleEquipement, false], // déjà sur la ligne : pas de raison
      ['NVS', lu.nvs, true],
      ['Code', e.designation, true],
      ['Chef de projet', e.chefProjet, true],
      ['Client', e.client, true],
    ]
    const autre = autres.find(([, v]) => cleRapprochement(v).includes(cle))
    if (autre) {
      trouves.push({ lu, rang: 2, raison: autre[2] ? `${autre[0]} : ${autre[1] ?? ''}` : undefined })
      continue
    }
    if (!chercheCaracteristique) continue
    const c = ref.caracteristiques.get(lu)?.find((x) => correspond(x, mots))
    if (c) trouves.push({ lu, rang: 3, raison: `${c.critere.libelle} : ${c.valeur}` })
  }
  return trouves
    .sort((a, b) => a.rang - b.rang || a.lu.equipement.numeroSerie.localeCompare(b.lu.equipement.numeroSerie))
    .slice(0, n)
    .map((t) => ({ genre: 'equipement' as const, equipement: t.lu.equipement, ...(t.raison ? { raison: t.raison } : {}) }))
}

export function rechercherProjets(ref: ReferentielIndexe, issues: readonly Issue[], q: string, n: number): Resultat[] {
  const cle = cleRapprochement(q)
  if (cle.length < LONGUEUR_MIN) return []
  const projets = new Map<string, { pn: string; cve: string; chef: string; client: string; nbEquipements: number }>()
  for (const { equipement: e } of ref.lus) {
    const k = cleRapprochement(e.pn)
    if (!k) continue
    const p = projets.get(k) ?? { pn: e.pn.trim(), cve: '', chef: '', client: '', nbEquipements: 0 }
    p.nbEquipements += 1
    p.cve ||= e.cve?.trim() ?? ''
    p.chef ||= e.chefProjet?.trim() ?? ''
    p.client ||= e.client?.trim() ?? ''
    projets.set(k, p)
  }
  // Rang : 0 = PN exact, 1 = PN ou CVE, 2 = client (« bachem » → ses projets).
  const rang = (k: string, p: { cve: string }): number =>
    k === cle ? 0 : k.includes(cle) || cleRapprochement(p.cve).includes(cle) ? 1 : 2
  return [...projets.entries()]
    .filter(([k, p]) => k.includes(cle) || cleRapprochement(p.cve).includes(cle) || cleRapprochement(p.client).includes(cle))
    .sort(([a, pa], [b, pb]) => rang(a, pa) - rang(b, pb) || a.localeCompare(b))
    .slice(0, n)
    .map(([k, p]) => ({
      genre: 'projet' as const,
      ...p,
      nbActifs: issues.filter((i) => estActif(i) && cleRapprochement(i.pn) === k).length,
    }))
}

export function rechercherChefs(ref: ReferentielIndexe, q: string, n: number): Resultat[] {
  const t = normaliser(q)
  if (t.length < LONGUEUR_MIN) return []
  const chefs = new Map<string, { projets: Set<string>; nbEquipements: number }>()
  for (const { equipement: e } of ref.lus) {
    const nom = e.chefProjet?.trim()
    if (!nom) continue
    const c = chefs.get(nom) ?? { projets: new Set<string>(), nbEquipements: 0 }
    c.nbEquipements += 1
    if (e.pn.trim()) c.projets.add(cleRapprochement(e.pn))
    chefs.set(nom, c)
  }
  return [...chefs.entries()]
    .filter(([nom]) => normaliser(nom).includes(t))
    // Le début d'un mot d'abord : « goy » trouve Lucile Goy avant Hugo Goyard… sinon l'ordre alphabétique.
    .sort(
      ([a], [b]) =>
        Number(normaliser(b).split(' ').some((m) => m.startsWith(t))) -
          Number(normaliser(a).split(' ').some((m) => m.startsWith(t))) || a.localeCompare(b, 'fr'),
    )
    .slice(0, n)
    .map(([nom, c]) => ({ genre: 'chef' as const, nom, nbProjets: c.projets.size, nbEquipements: c.nbEquipements }))
}

/**
 * « atex 1 » → Zone dangereuse : ATEX zone 1 (6 équipements). Une valeur égale
 * au texte tapé d'abord, puis les critères de la barre de raccourcis, puis les
 * plus fréquentes.
 */
export function rechercherFiltres(ref: ReferentielIndexe, q: string, n: number): Resultat[] {
  const t = normaliser(q)
  if (t.length < LONGUEUR_MIN_CARACTERISTIQUE) return []
  const mots = t.split(' ')
  const comptes = new Map<string, { c: Caracteristique; nb: number }>()
  for (const liste of ref.caracteristiques.values()) {
    for (const c of liste) {
      if (!correspond(c, mots)) continue
      const k = `${c.critere.id}\u0000${c.valeur}`
      const x = comptes.get(k)
      if (x) x.nb += 1
      else comptes.set(k, { c, nb: 1 })
    }
  }
  const rangRaccourci = (id: string): number => {
    const i = RACCOURCIS.indexOf(id)
    return i === -1 ? RACCOURCIS.length : i
  }
  return [...comptes.values()]
    .sort(
      (a, b) =>
        Number(b.c.valeurNormalisee === t) - Number(a.c.valeurNormalisee === t) ||
        rangRaccourci(a.c.critere.id) - rangRaccourci(b.c.critere.id) ||
        b.nb - a.nb,
    )
    .slice(0, n)
    .map(({ c, nb }) => ({ genre: 'filtre' as const, critere: c.critere.id, libelle: c.critere.libelle, valeur: c.valeur, nb }))
}

/**
 * Les FNC-T (lot 5 du § 14, 22/10) : par leur N°, un N° de série, le CVE, un mot
 * de la description, des documents impactés, d'un écart relevé, ou le leader. La
 * `raison` dit où le mot a été trouvé quand ça ne se lit pas sur la ligne. Le N°
 * exact d'abord, puis les ouvertes (et relevés) avant les closes, les plus récentes d'abord.
 */
export function rechercherFnct(fncts: readonly Fnct[], q: string, n: number): Resultat[] {
  const cle = cleRapprochement(q)
  if (cle.length < LONGUEUR_MIN) return []
  const texte = normaliser(q)
  const contient = (v: string | undefined): boolean => normaliser(v).includes(texte) || cleRapprochement(v).includes(cle)
  const trouves: { f: Fnct; raison?: string; exact: boolean }[] = []
  for (const f of fncts) {
    const titre = (f.description.split(/\r?\n/).find((l) => l.trim()) ?? '').trim()
    const direct = [f.numero, titre, ...f.numerosSerie].some((v) => contient(v))
    let raison: string | undefined
    if (!direct) {
      const ecart = ecartsLisibles(f.ecarts).find((e) => contient(phraseDe(e)))
      if (contient(f.description)) raison = 'Dans la description'
      else if (contient(f.cve)) raison = `CVE : ${f.cve}`
      else if (contient(f.documentsImpactes)) raison = 'Dans les documents impactés'
      else if (ecart) raison = `Écart : ${phraseDe(ecart)}`
      else if (contient(f.leader)) raison = `Leader : ${f.leader}`
      else continue
    }
    trouves.push({ f, raison, exact: cleRapprochement(f.numero) === cle })
  }
  const rang = (f: Fnct): number => (f.cloture ? 1 : 0)
  return trouves
    .sort((a, b) => Number(b.exact) - Number(a.exact) || rang(a.f) - rang(b.f) || (debutFnct(b.f) ?? '').localeCompare(debutFnct(a.f) ?? ''))
    .slice(0, n)
    .map(({ f, raison }) => {
      const premier = (f.description.split(/\r?\n/).find((l) => l.trim()) ?? '').trim()
      const nb = ecartsLisibles(f.ecarts).length
      return {
        genre: 'fnct' as const,
        numero: f.numero,
        titre: premier || (nb ? `Relevé P&ID / PPL · ${nb} écart${nb > 1 ? 's' : ''}` : f.numero),
        machines: f.numerosSerie.length > 1 ? `${f.numerosSerie[0]} + ${f.numerosSerie.length - 1}` : (f.numerosSerie[0] ?? ''),
        etat: f.cloture ? ('close' as const) : estEnReleve(f) ? ('releve' as const) : ('ouverte' as const),
        raison,
      }
    })
}

/** Tout, dans l'ordre d'affichage. Sans référentiel (fournisseur), les problèmes seuls ; sans accès, pas de FNC-T. */
export function rechercher(q: string, issues: readonly Issue[], ref: ReferentielIndexe, fncts: readonly Fnct[] = []): Resultat[] {
  return [
    ...rechercherProjets(ref, issues, q, LIMITES.projet),
    ...rechercherChefs(ref, q, LIMITES.chef),
    ...rechercherFiltres(ref, q, LIMITES.filtre),
    ...rechercherProblemes(issues, q, LIMITES.probleme),
    ...rechercherFnct(fncts, q, LIMITES.fnct),
    ...rechercherEquipements(ref, q, LIMITES.equipement),
  ]
}

export const TITRES_GENRES: Record<Resultat['genre'], string> = {
  projet: 'Projets',
  chef: 'Chefs de projet',
  filtre: 'Filtrer les équipements',
  probleme: 'Problèmes',
  fnct: 'FNC-T',
  equipement: 'Équipements',
}
